import { useRef, useState } from 'react'

export interface NotificationState {
  isOpen: boolean
  title: string
  message: string
  variant: 'success' | 'error' | 'info'
  copyableText?: string
  copyLabel?: string
  details?: string
}

export interface ConfirmState {
  isOpen: boolean
  title: string
  message: string
  variant: 'danger' | 'warning' | 'default'
  confirmText: string
  onConfirm: () => void
}

export interface Feedback {
  notification: NotificationState
  confirm: ConfirmState
  toast: { message: string; visible: boolean }
  showNotification(
    variant: 'success' | 'error' | 'info',
    title: string,
    message: string,
    options?: { copyableText?: string; copyLabel?: string; details?: string },
  ): void
  closeNotification(): void
  showConfirm(
    title: string,
    message: string,
    onConfirm: () => void,
    options?: { variant?: 'danger' | 'warning' | 'default'; confirmText?: string },
  ): void
  closeConfirm(): void
  showToast(message: string): void
}

export function useFeedback(): Feedback {
  // Modal states
  const [notification, setNotification] = useState<NotificationState>({
    isOpen: false,
    title: '',
    message: '',
    variant: 'info'
  })
  const [confirmModal, setConfirmModal] = useState<ConfirmState>({
    isOpen: false,
    title: '',
    message: '',
    variant: 'default',
    confirmText: 'Confirm',
    onConfirm: () => {}
  })

  // Toast notification state
  const [toast, setToast] = useState<{ message: string; visible: boolean }>({ message: '', visible: false })
  const toastTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const showToast = (message: string) => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current)
    setToast({ message, visible: true })
    toastTimeoutRef.current = setTimeout(() => {
      setToast(prev => ({ ...prev, visible: false }))
    }, 3000)
  }

  // Helper to show notifications
  const showNotification = (
    variant: 'success' | 'error' | 'info',
    title: string,
    message: string,
    options?: { copyableText?: string; copyLabel?: string; details?: string }
  ) => {
    setNotification({
      isOpen: true,
      variant,
      title,
      message,
      ...options
    })
  }

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, isOpen: false }))
  }

  // Helper to show confirm modal
  const showConfirm = (
    title: string,
    message: string,
    onConfirm: () => void,
    options?: { variant?: 'danger' | 'warning' | 'default'; confirmText?: string }
  ) => {
    setConfirmModal({
      isOpen: true,
      title,
      message,
      onConfirm,
      variant: options?.variant || 'default',
      confirmText: options?.confirmText || 'Confirm'
    })
  }

  const closeConfirm = () => {
    setConfirmModal(prev => ({ ...prev, isOpen: false }))
  }

  return {
    notification, confirm: confirmModal, toast,
    showNotification, closeNotification, showConfirm, closeConfirm, showToast,
  }
}
